import { useEffect, MutableRefObject } from 'react';

const MAX_DAILY_HOURS = 4 * 3600;
const BREAK_REQUIRED_AFTER = 2 * 3600;

interface UseMasterClockProps {
  isActive: boolean;
  isPaused: boolean;
  isOnBreak: boolean;
  setSessionTime: React.Dispatch<React.SetStateAction<number>>;
  setTotalTime: React.Dispatch<React.SetStateAction<number>>;
  setTotalCourseTime: React.Dispatch<React.SetStateAction<number>>;
  setLessonTime: React.Dispatch<React.SetStateAction<number>>;
  setIsLockedOut: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
  setBreakRequired: React.Dispatch<React.SetStateAction<boolean>>;
  sessionTimeRef: MutableRefObject<number>;
  totalTimeRef: MutableRefObject<number>;
  totalCourseTimeRef: MutableRefObject<number>;
  lessonTimeRef: MutableRefObject<number>;
  lastBreakTimeRef: MutableRefObject<number>;
  breakCheckRef: MutableRefObject<boolean>;
  lastTickRef: MutableRefObject<number | null>;
  intervalRef: MutableRefObject<ReturnType<typeof setInterval> | null>;
  saveIntervalRef: MutableRefObject<ReturnType<typeof setInterval> | null>;
  saveSessionProgress: () => Promise<void>;
  videoProgress: any;
  lessonsAccessed: string[];
  breakHistory: any[];
}

export const useMasterClock = ({
  isActive,
  isPaused,
  isOnBreak,
  setSessionTime,
  setTotalTime,
  setTotalCourseTime,
  setLessonTime,
  setIsLockedOut,
  setIsActive,
  setIsPaused,
  setBreakRequired,
  sessionTimeRef,
  totalTimeRef,
  totalCourseTimeRef,
  lessonTimeRef,
  lastBreakTimeRef,
  breakCheckRef,
  lastTickRef,
  intervalRef,
  saveIntervalRef,
  saveSessionProgress,
  videoProgress,
  lessonsAccessed,
  breakHistory
}: UseMasterClockProps) => {
  // Master Clock Heartbeat Interval
  useEffect(() => {
    if (isActive && !isPaused && !isOnBreak) {
      if (!lastTickRef.current) {
        lastTickRef.current = Date.now();
      }

      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const deltaMs = now - (lastTickRef.current || now);
        const deltaSeconds = Math.floor(deltaMs / 1000);

        if (deltaSeconds > 0) {
          // 1. Advance the clock ref
          lastTickRef.current = (lastTickRef.current || now) + deltaSeconds * 1000;

          // 2. Update Session Time (Physical duration in tab)
          setSessionTime(prev => {
            const newSessionTime = prev + deltaSeconds;
            sessionTimeRef.current = newSessionTime;

            // Compliance Check: 2-Hour Mandatory Break Trigger
            if (newSessionTime - lastBreakTimeRef.current >= BREAK_REQUIRED_AFTER && !breakCheckRef.current) {
              console.warn('[TimerContext] 2-hour compliance threshold reached. Triggering break.');
              breakCheckRef.current = true;
              setBreakRequired(true);
            }

            return newSessionTime;
          });

          // 3. Update Total Daily Time (Enforcement)
          setTotalTime(prev => {
            const newTotalTime = prev + deltaSeconds;

            // Compliance Check: 4-Hour Daily Limit
            if (newTotalTime >= MAX_DAILY_HOURS) {
              console.error('[TimerContext] 4-hour daily limit reached. Locking out student.');
              setIsLockedOut(true);
              setIsActive(false);
              setIsPaused(false);
            }

            totalTimeRef.current = newTotalTime;
            return newTotalTime;
          });

          // 4. Update Course Total & Lesson Credit (Instructional Time)
          setTotalCourseTime(prev => {
            const newVal = prev + deltaSeconds;
            totalCourseTimeRef.current = newVal;
            return newVal;
          });
          setLessonTime(prev => {
            const newVal = prev + deltaSeconds;
            lessonTimeRef.current = newVal;
            return newVal;
          });
        }
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      lastTickRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isPaused, isOnBreak]);

  // Periodic Firestore progress save (every 30 seconds)
  useEffect(() => {
    if (isActive && !isPaused) {
      saveIntervalRef.current = setInterval(() => {
        saveSessionProgress();
      }, 30000);
    } else {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    }

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isPaused, videoProgress, lessonsAccessed, breakHistory]);
};
